import React, { useState, useEffect } from "react";
import { Route, BrowserRouter, Routes } from "react-router-dom";
import ReactGA from 'react-ga4';

import "./custom.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { LanguageProvider } from "./components/contexts/language-state/LanguageContext";
import { Home } from "./components/pages/Home";
import { AdminLoginScreen } from "./components/AdminComponents/AdminLoginScreen";
import { PageNotFound } from "./components/pages/PageNotFound";
import { InstructionsPage } from "./components/pages/InstructionsPage";
import { Layout } from "./components/Layout";
import { SideBarMenu, Dropdown, StatementList } from "./components/SideBarMenu";
import { SplitScreen } from "./components/SplitScreen";
import { StatementDetails } from "./components/StatementDetails";
import { uriTransformer } from "./components/custom-functions/UriTransformer";

import { getMenus } from "./data-sources/api";

/**
 * Main entry point of the application, setting up routes and initializing data.
 * 
 * @returns {JSX.Element} The main application component with routing and layout.
 */
export function App() {
  const [menu, setMenu] = useState(null);

  /*
   * Fetches menu data on component render
   */
  useEffect(() => {
    getMenus()
      .then((response) => {
        setMenu(response.data);
      })
      .catch((error) => {
        console.error("Error fetching menu data:", error);
      });

      // Initialize Google Analytics
      ReactGA.initialize("G-F23KFFLT84");
  }, []);

  return (
    <BrowserRouter basename="/">
      <LanguageProvider>
        <Routes>
          <Route path="/" element={<Layout />} >
            <Route index path="/" element={<Home />} />
            <Route path="*" element={<PageNotFound />} />
            <Route path="/instruktioner" element={<InstructionsPage />} />

            {/* Generate dynamic routes depending on chosen menu, e.g. "Hälsningar" */}
            {menu && menu.map((menuItem) => {
              const basePath = `${uriTransformer(menuItem.menuText)}`;

              return (
                <Route
                  key={menuItem.id}
                  path={basePath}
                  element={
                    <SplitScreen
                      left={() =>
                        <SideBarMenu
                          menuChildren={menuItem.menuChildren}
                          menuId={menuItem.id}
                        />}
                      right={StatementDetails}
                    />
                  }
                >
                  {/* Generate dynamic routes for submenus, e.g. "Hälsningsfraser" */}
                  {menuItem.menuChildren &&
                    menuItem.menuChildren.map((subMenuItem) => {
                      const subMenuPath = `${uriTransformer(subMenuItem.menuText)}`;

                      return (
                        // Generate routes for second level dropdowns or get statement list items of first level dropdown menu if no more nested subject categories is related
                        <React.Fragment key={subMenuItem.id}>
                          {subMenuItem.menuChildren &&
                            subMenuItem.menuChildren.length > 0 ? (
                            <Route
                              key={subMenuItem.id}
                              path={subMenuPath}
                              element={
                                <Dropdown
                                  menuChildren={subMenuItem.menuChildren}
                                  menuId={subMenuItem.id}
                                  customClass={`sidebar-items dropdown-children light-text`}
                                />
                              }
                            >
                              {/*Generate dynamic routes for subsubmenus, e.g. Obstetrisk anamnes*/}
                              <React.Fragment>
                                {subMenuItem.menuChildren &&
                                  subMenuItem.menuChildren.map((statements) => {
                                    const subMenuStatementPath = `${uriTransformer(statements.menuText)}`

                                    return (
                                      <Route
                                        key={statements.id}
                                        path={subMenuStatementPath}
                                        element={
                                          <StatementList subMenuId={statements.id} />
                                        } />
                                    )
                                  })}
                              </React.Fragment>
                            </Route>
                          ) : (
                            <Route
                              key={subMenuItem.id}
                              path={subMenuPath}
                              element={
                                <StatementList subMenuId={subMenuItem.id} />
                              }
                            />
                          )}
                        </React.Fragment>
                      )
                    })
                  }
                </Route>
              );
            })};
          </Route>
          <Route path="/sadima-login" element={<AdminLoginScreen />} />
        </Routes>
      </LanguageProvider>
    </BrowserRouter>
  );
}
